import { ztHash, ztHashHex } from './zeroTTSSelector';
import { ensureXxhash, parseHexSeed } from './xxhash';
import { isValidKokoroVoiceId, resolveKokoroVoice } from './voiceMapping';
import type { KokoroVoiceId } from './voiceMapping';
import type { KokoroGender, VoiceSpec } from './types';

export interface Context {
  templates: string[];
  tier: 1 | 2 | 3;
  trigger_hints?: string[];
  prosody_tag?: string;
}

export interface TTSProfile {
  name: string;
  persona_seed: string;
  description: string;
  version: string;

  voice_blend: {
    strategy: 'single' | 'hash_blend' | 'explicit_weights';
    base_voices: string[];
    blend_seed?: string;
    gender_hint?: KokoroGender;
    world_seed_hex?: string;
  };

  contexts: Record<string, Context>;
  pools: Record<string, string[]>;

  install: {
    strategy: 'eager' | 'tiered';
    priority_contexts?: string[];
    max_atoms?: number;
  };

  runtime_requirements?: {
    coop?: string;
    coep?: string;
    kokoro_device?: string;
    kokoro_dtype?: string;
    shared_array_buffer?: boolean;
  };
}

export interface AtomEntry {
  atom_text: string;
  context_name: string;
  tier: 1 | 2 | 3;
  cache_key: string;
  status: 'rendered' | 'failed' | 'pending';
  byte_size?: number;
  duration_ms?: number;
}

/**
 * Stage 26.6: how a persona's atoms were (or will be) rendered. Keeping the
 * identity as a discriminated union lets blend-mode pins flow end-to-end
 * through the installer, manifest, and runtime-fallback paths without any
 * of them having to guess from a voice_id string.
 */
export type VoiceIdentity =
  | { kind: 'single'; voiceId: KokoroVoiceId }
  | { kind: 'blend'; spec: VoiceSpec; worldSeedHex: string };

export interface AtomManifest {
  persona_seed: string;
  profile_version: string;
  installed_at: number;
  /**
   * Stage 26: the Kokoro base voice id used to render atoms. Populated for
   * BOTH single- and blend-mode installs; for blend mode it records the
   * underlying voiceA id so manifests from earlier builds remain readable.
   */
  voice_id: string;
  /** Stage 26.6: full voice identity this manifest was rendered against.
   *  Missing on manifests produced by Stage 26. */
  voice_identity?: VoiceIdentity;
  /** Stage 26.6: 16-char hex fingerprint of voice_identity. Used to scope
   *  the atom-cache bucket so pin / mode changes invalidate automatically. */
  voice_fingerprint?: string;
  atoms: Record<string, AtomEntry>;
}

export function personaSeedFromName(
  name: string | null | undefined,
): string {
  return (name ?? '').trim().toLowerCase();
}

/**
 * Stage 40: cache-depth selector. Each depth points at a separate
 * profile JSON in /zerotts/. Classic preserves the original filename
 * (no suffix) so any pre-Stage-40 cached manifests still resolve.
 */
export type ZeroTTSDepth = 'classic' | 'medium' | 'long';

export function asZeroTTSDepth(raw: unknown): ZeroTTSDepth {
  return raw === 'medium' || raw === 'long' ? raw : 'classic';
}

export async function loadProfile(
  personaSeed: string,
  depth: ZeroTTSDepth = 'classic',
): Promise<TTSProfile | null> {
  if (!personaSeed) return null;
  // Classic keeps the legacy filename; medium/long use a suffix.
  const filename =
    depth === 'classic'
      ? `${encodeURIComponent(personaSeed)}_tts_profile.json`
      : `${encodeURIComponent(personaSeed)}_${depth}_tts_profile.json`;
  let res: Response;
  try {
    res = await fetch(`/zerotts/${filename}`, { cache: 'no-cache' });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      `[zerotts] profile fetch failed for "${personaSeed}/${depth}":`,
      err,
    );
    return null;
  }
  if (!res.ok) return null;
  const json = (await res.json()) as TTSProfile;
  if (!json.persona_seed || !json.contexts || !json.pools) {
    throw new Error(
      `[zerotts] invalid profile for "${personaSeed}/${depth}"`,
    );
  }
  if (json.persona_seed !== personaSeed) {
    // eslint-disable-next-line no-console
    console.warn(
      `[zerotts] profile persona_seed "${json.persona_seed}" does not match requested "${personaSeed}" — honouring file value`,
    );
  }
  return json;
}

export async function resolveVoiceId(
  profile: TTSProfile,
  worldSeedHex: string,
  defaultGender: KokoroGender,
): Promise<KokoroVoiceId> {
  await ensureXxhash();
  const { strategy, base_voices, blend_seed, gender_hint } = profile.voice_blend;

  if (strategy !== 'single') {
    // eslint-disable-next-line no-console
    console.warn(
      `[zerotts] voice_blend.strategy "${strategy}" not supported in v1; falling back to single`,
    );
  }

  if (base_voices.length === 1 && isValidKokoroVoiceId(base_voices[0])) {
    return base_voices[0];
  }

  if (
    base_voices.length > 1 &&
    base_voices.every((v) => isValidKokoroVoiceId(v))
  ) {
    const seed = blend_seed ?? profile.persona_seed;
    const h = ztHash(seed, 'voice');
    const idx = Number(h % BigInt(base_voices.length));
    return base_voices[idx] as KokoroVoiceId;
  }

  const gender: KokoroGender = gender_hint ?? defaultGender;
  return resolveKokoroVoice({
    name: profile.persona_seed,
    gender,
    worldSeed: parseHexSeed(worldSeedHex || '0'),
  });
}

export interface ResolveVoiceIdentityInput {
  profile: TTSProfile;
  worldSeedHex: string;
  defaultGender: KokoroGender;
  /** Stage 26.6: blend-mode pin captured by the user. When present AND
   *  blendMode === 'blend', atoms render through the blend pipeline using
   *  this exact spec so the cached filler matches the live reply voice. */
  pinnedSpec: VoiceSpec | null;
  /** 'single' | 'blend' — mirrors the kokoro_blend_mode setting. */
  blendMode: 'single' | 'blend';
}

export async function resolveVoiceIdentity(
  input: ResolveVoiceIdentityInput,
): Promise<VoiceIdentity> {
  const { profile, worldSeedHex, defaultGender, pinnedSpec, blendMode } = input;
  if (blendMode === 'blend' && pinnedSpec) {
    return { kind: 'blend', spec: pinnedSpec, worldSeedHex };
  }
  const voiceId = await resolveVoiceId(profile, worldSeedHex, defaultGender);
  return { kind: 'single', voiceId };
}

/** Stable 16-char hex fingerprint for a voice identity. Changes whenever
 *  any component of the identity changes, so the atom cache bucket keyed
 *  by this fingerprint auto-invalidates on pin/mode changes.
 *
 *  Stage 40: depth is folded in so switching between classic/medium/long
 *  also invalidates the bucket — same mechanism that pin/blend changes
 *  ride on. Default 'classic' preserves the bucket key for any caller
 *  that hasn't been updated to pass depth. */
export function fingerprintVoiceIdentity(
  id: VoiceIdentity,
  depth: ZeroTTSDepth = 'classic',
): string {
  if (id.kind === 'single') {
    return depth === 'classic'
      ? ztHashHex('single', id.voiceId)
      : ztHashHex('single', id.voiceId, depth);
  }
  const s = id.spec;
  const baseCoords: (string | number)[] = [
    'blend',
    id.worldSeedHex || '0',
    s.voiceA,
    s.voiceB,
    s.t.toFixed(6),
    s.pitch.toFixed(4),
    s.energy.toFixed(4),
  ];
  return depth === 'classic'
    ? ztHashHex(...baseCoords)
    : ztHashHex(...baseCoords, depth);
}

/** Resolve the underlying base voice id for an identity — used for manifest
 *  back-compat and for logging. For blend identities this is voiceA. */
export function baseVoiceIdForIdentity(id: VoiceIdentity): string {
  if (id.kind === 'single') return id.voiceId;
  // Import KOKORO_VOICES lazily via require-style destructure would break
  // circular-import boundaries; importers already have the mapping when
  // they need the string form. Return the numeric voiceA encoded so callers
  // that want the base-voice string can look it up from voiceMapping.
  return `blend_voiceA_${id.spec.voiceA}`;
}
